//@ts-nocheck
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import { formatISO, differenceInDays, addDays, subDays } from 'date-fns';
import { Box } from '@mui/material';
import { Sparkle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createTrip } from '../../../services/trip/trip';
import {
  streamTripImportDraft,
  commitTripImport,
  clearSessionAndGoToLogin
} from '../../../services/tripImport/tripImport';
import { useNavigate } from 'react-router-dom';
import { ErrorToast } from '../../../utils/alerts';
import { blue } from '@mui/material/colors';
import { normalizeTravelDate } from '../../../utils/travelDate';
import {
  XGButton,
  XGModal,
  XGInput,
  XGAutocomplete,
  MultiDateRangePicker
} from 'xg-cl';
import XGTextarea from '../../ProseTextarea/ProseTextarea';
import TripImportSection from './TripImport/TripImportSection';
import TripImportProgress from './TripImport/TripImportProgress';
import TripImportSummary from './TripImport/TripImportSummary';
import * as PropTypes from 'prop-types';
import './CreateTrip.css';

const INITIAL_IMPORT_VALUE = { text: '', file: null, url: '' };

/**
 * Functional React component for rendering a custom modal content to create a trip.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {Array} [props.destinations] - Array with destinations objects.
 * @param {string} [props.id] - The ID for the trip create trip component.
 * @param {...any} props.rest - Additional props to be spread on the Box element.
 *
 * @returns {JSX.Element} React element representing the trip creating modal.
 */

const CreateTrip = ({ destinations, id, openModal, setOpenModal, ...rest }) => {
  const initialFormState = {
    title: '',
    dateRange: [null, null],
    duration: '1',
    destination: null,
    description: '',
    outline: ''
  };
  const navigate = useNavigate();
  const { t } = useTranslation(['trip', 'common']);
  const { enableTripImportAgent, enableTripImportDocuments } = useFlags();
  const showImport =
    !!enableTripImportAgent ||
    (import.meta.env.DEV &&
      import.meta.env.VITE_ENABLE_TRIP_IMPORT_AGENT === 'true');
  const allowDocuments =
    showImport &&
    (enableTripImportDocuments === true ||
      (import.meta.env.DEV &&
        import.meta.env.VITE_ENABLE_TRIP_IMPORT_DOCUMENTS === 'true'));

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [formError, setFormError] = useState({
    title: '',
    duration: '',
    destination: ''
  });
  const [progress, setProgress] = useState(0);
  const [submitPressed, setSubmitPressed] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [durationTouched, setDurationTouched] = useState(false);

  // Trip import agent (B2/V3): controlled "Have something already?" box,
  // the draft SSE stream state, and the read-only "Here's what I did"
  // summary shown after the trip has already been created.
  const [importValue, setImportValue] = useState(INITIAL_IMPORT_VALUE);
  const [importStep, setImportStep] = useState('form'); // 'form' | 'reading' | 'creating' | 'summary'
  const [importProgress, setImportProgress] = useState(null);
  const [draft, setDraft] = useState(null);
  const [tripId, setTripId] = useState(null);
  const [importErrorCode, setImportErrorCode] = useState('');
  // 'empty' | 'valid' | 'invalid': what the import box's link input holds,
  // reported live (before it is committed on blur).
  const [linkStatus, setLinkStatus] = useState('empty');
  // Field errors show only once the operator has touched/blurred the field.
  const [touched, setTouched] = useState({ title: false, duration: false });
  const importAbortControllerRef = useRef(null);
  // Bumped whenever the current import run is cancelled, the modal is closed
  // or the component unmounts, so a late stream/commit result is ignored.
  const importRunRef = useRef(0);

  const hasImport =
    showImport &&
    Boolean(
      importValue.text.trim() ||
      (allowDocuments && importValue.file) ||
      importValue.url.trim() ||
      linkStatus === 'valid'
    );

  // A remotely disabled upload must not remain eligible for submission.
  useEffect(() => {
    if (!allowDocuments) {
      setImportValue((current) =>
        current.file ? { ...current, file: null } : current
      );
    }
  }, [allowDocuments]);

  useEffect(() => {
    let interval;
    if (submitPressed && formData.outline && progress < 100 && !isCancelled) {
      interval = setInterval(() => {
        setProgress((prevProgress) => Math.min(prevProgress + 1, 100));
      }, 200); // Frequent updates with small increments
    }

    return () => clearInterval(interval);
  }, [submitPressed, formData.outline, progress, isCancelled]);

  // Abort any in-flight draft stream if the component unmounts mid-read.
  useEffect(() => {
    return () => {
      importRunRef.current += 1;
      importAbortControllerRef.current?.abort();
    };
  }, []);

  const handleOnInputNumber = (evt) => {
    const { name, tagName } = evt.target;

    if (tagName.toLowerCase() === 'input') {
      evt.target.value = evt.target.value.replace(/[^0-9]/g, '');
      const duration = Number(evt.target.value);

      if (name === 'duration') {
        setDurationTouched(true);
      }

      setFormData((prevData) => ({ ...prevData, [name]: evt.target.value }));

      const { dateRange } = formData;

      if (
        dateRange &&
        dateRange.length == 2 &&
        dateRange[0] &&
        dateRange[1] &&
        duration
      ) {
        const daysDifference = differenceInDays(dateRange[1], dateRange[0]);

        //New duration is greater than date range days, proced to add days
        if (duration > daysDifference) {
          const newEndDate = addDays(
            dateRange[1],
            duration - daysDifference - 1
          );
          setFormData((prevData) => ({
            ...prevData,
            dateRange: [dateRange[0], newEndDate]
          }));
        }

        //New duration is less than date range days, proced to remove days
        if (daysDifference > duration) {
          const newEndDate = subDays(
            dateRange[1],
            daysDifference - duration + 1
          );

          setFormData((prevData) => ({
            ...prevData,
            dateRange: [dateRange[0], newEndDate]
          }));
        }

        //New duration is equal to 1, proced to set equal dates
        if (duration === 1) {
          setFormData((prevData) => ({
            ...prevData,
            dateRange: [dateRange[0], dateRange[0]]
          }));
        }
      }
    }
  };

  const handleChange = (evt) => {
    const { name, value } = evt.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleOnDateRangeChange = (selectedDateRange = [null, null]) => {
    setFormData((prevData) => ({
      ...prevData,
      dateRange: selectedDateRange
    }));

    if (
      selectedDateRange?.length == 2 &&
      selectedDateRange[0] &&
      selectedDateRange[1]
    ) {
      const daysDifference = differenceInDays(
        selectedDateRange[1],
        selectedDateRange[0]
      );

      setDurationTouched(true);
      setFormData((prevData) => ({
        ...prevData,
        duration: `${daysDifference + 1}`
      }));
    }
  };

  const isDurationValid = (duration) =>
    !isNaN(duration) && Number.isInteger(Number(duration)) && duration >= 1;

  const markTouched = (field) =>
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));

  const validateForm = () => {
    setFormError({
      title: '',
      duration: '',
      destination: ''
    });

    if (!hasImport) {
      if (!formData.title || !formData.title.trim()) {
        setFormError((prevData) => ({
          ...prevData,
          title: t('pleaseAddTripTitle')
        }));
        return;
      }

      if (!formData.duration || !formData.duration.trim()) {
        setFormError((prevData) => ({
          ...prevData,
          duration: t('pleaseAddTripDuration')
        }));
        return;
      }
    }

    if (
      formData.duration &&
      `${formData.duration}`.trim() &&
      !isDurationValid(formData.duration)
    ) {
      setFormError((prevData) => ({
        ...prevData,
        duration: t('pleaseAddValidTripDuration')
      }));
      return;
    }

    if (formData.outline && !formData.destination) {
      setFormError((prevData) => ({
        ...prevData,
        destination: t('pleaseAddDestination')
      }));
      return;
    }

    return true;
  };

  const buildImportFields = () => {
    const fields = {};

    if (formData.title && formData.title.trim()) {
      fields.title = formData.title.trim();
    }

    if (formData.dateRange?.[0]) {
      const normalizedStartDate = normalizeTravelDate(formData.dateRange[0]);
      if (normalizedStartDate) {
        fields.startDate = normalizedStartDate;
      }
    }

    // Only send a duration the operator actually chose. The field defaults
    // to '1' for the untouched blank-trip flow, and sending that default
    // along with an import would silently clamp every imported trip to a
    // single day.
    if (durationTouched && isDurationValid(formData.duration)) {
      fields.duration = Number(formData.duration);
    }

    if (formData.destination) {
      const { countryId, cityId, title } = formData.destination;
      fields.destination = { countryId, cityId, title };
    }

    return fields;
  };

  const getImportSourceKind = () => {
    if (importValue.file) {
      const name = importValue.file.name?.toLowerCase() || '';
      const type = importValue.file.type || '';
      if (name.endsWith('.docx') || type.includes('wordprocessingml')) {
        return 'docx';
      }
      return 'pdf';
    }
    if (importValue.url && importValue.url.trim()) {
      return 'url';
    }
    return 'text';
  };

  const handleImportValueChange = (nextValue) => {
    setImportValue({
      ...nextValue,
      file: allowDocuments ? nextValue.file : null
    });
    if (importErrorCode) {
      setImportErrorCode('');
    }
  };

  const startTripImport = async () => {
    setImportErrorCode('');
    setDraft(null);
    setTripId(null);
    setImportProgress(null);
    setImportStep('reading');

    const controller = new AbortController();
    importAbortControllerRef.current = controller;
    const runId = importRunRef.current + 1;
    importRunRef.current = runId;
    // True once this run was cancelled, the modal closed or we unmounted: the
    // stream/commit may still resolve, but must not commit or touch state.
    const isStale = () =>
      runId !== importRunRef.current || controller.signal.aborted;
    // The draft lives in a local variable, not state: state updates are
    // async, and we need the draft the instant the stream ends so we can
    // commit it immediately, with no summary step in between.
    let receivedDraft = null;

    try {
      await streamTripImportDraft(
        {
          fields: buildImportFields(),
          text: importValue.text,
          url: importValue.url,
          file: allowDocuments ? importValue.file : null
        },
        {
          signal: controller.signal,
          onEvent: (event) => {
            if (isStale()) {
              return;
            }
            if (event.type === 'progress') {
              setImportProgress(event);
            } else if (event.type === 'draft') {
              receivedDraft = event.draft;
            } else if (event.type === 'error') {
              setImportErrorCode(event.code);
            }
          }
        }
      );
    } catch (err) {
      if (importAbortControllerRef.current === controller) {
        importAbortControllerRef.current = null;
      }
      if (!isStale() && err?.name !== 'AbortError') {
        setImportErrorCode(err?.code || 'INTERNAL');
        setImportStep('form');
      }
      return;
    }

    if (importAbortControllerRef.current === controller) {
      importAbortControllerRef.current = null;
    }

    // Cancelled right as the draft arrived: do nothing (no commit, no state).
    if (isStale()) {
      return;
    }

    if (!receivedDraft) {
      // An `error` event already set importErrorCode; just return to the form.
      setImportStep('form');
      return;
    }

    setDraft(receivedDraft);
    setImportStep('creating');

    try {
      const { tripId: newTripId, warnings } =
        await commitTripImport(receivedDraft);

      if (isStale()) {
        return;
      }

      if (!newTripId) {
        throw new Error('Missing tripId');
      }

      // Things that failed while creating the trip (e.g. an image that would
      // not save) go first in the summary so they are never hidden behind
      // "Show more".
      if (warnings?.length) {
        setDraft((current) =>
          current
            ? { ...current, notes: [...warnings, ...(current.notes || [])] }
            : current
        );
      }

      setTripId(newTripId);
      setImportStep('summary');
    } catch (err) {
      if (isStale()) {
        return;
      }
      // The draft is lost on a commit failure; an expired session gets its
      // own message saying so.
      setImportErrorCode(
        err?.code === 'SESSION_EXPIRED'
          ? 'COMMIT_SESSION_EXPIRED'
          : 'COMMIT_FAILED'
      );
      setImportStep('form');
    }
  };

  const handleConfirm = async (evt) => {
    evt.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (hasImport) {
      await startTripImport();
      return;
    }

    setLoading(true);
    setSubmitPressed(true);
    setIsCancelled(false);

    try {
      const payload = {
        ...formData,
        name: formData.title,
        startDate:
          formData.dateRange &&
          formData.dateRange.length &&
          formData.dateRange[0]
            ? formatISO(formData.dateRange[0]).split('T')[0]
            : null,
        duration: Number(formData.duration),
        description: '',
        outline: formData.outline
      };

      const result = await createTrip(payload);

      if (result.error || isCancelled) {
        ErrorToast(t('tripNotCreatedTryAgain'));
        setLoading(false);
        setProgress(0); // Reset progress
        setSubmitPressed(false); // Reset submit pressed state
        return;
      }

      if (result.data && result.data.tripId) {
        navigate(`/trips/${result.data.tripId}`);
      }
    } catch (err) {
      if (!isCancelled) {
        ErrorToast(t('tripNotCreatedTryAgain'));
      }
    }

    setLoading(false);
    setProgress(0); // Reset progress
    setSubmitPressed(false); // Reset submit pressed state
  };

  const resetAndCloseModal = () => {
    importRunRef.current += 1;
    setIsCancelled(true);
    setLoading(false);
    setSubmitPressed(false);
    setProgress(0);
    setFormData(initialFormState);
    setDurationTouched(false);
    setTouched({ title: false, duration: false });
    setImportValue(INITIAL_IMPORT_VALUE);
    setImportStep('form');
    setImportProgress(null);
    setDraft(null);
    setTripId(null);
    setImportErrorCode('');
    setOpenModal(false);
  };

  const handleCancel = () => {
    if (importStep === 'reading') {
      importRunRef.current += 1;
      importAbortControllerRef.current?.abort();
      importAbortControllerRef.current = null;
      setImportStep('form');
      setImportProgress(null);
      setImportErrorCode('');
      return;
    }

    // The commit is already in flight: nothing to abort, and the Cancel
    // button is disabled for this step, so this shouldn't normally fire.
    if (importStep === 'creating') {
      return;
    }

    resetAndCloseModal();
  };

  // Called by TripImportSummary once it has deleted the just-created trip.
  const handleDiscarded = () => {
    resetAndCloseModal();
  };

  // With the import box shown, the primary button is disabled until the
  // form can actually be submitted (manual path: title + valid duration;
  // import path: a link or file, and a typed duration must still be valid).
  // With the flag off, today's behaviour is kept exactly (submit-time
  // validation only).
  const durationRaw = `${formData.duration ?? ''}`.trim();
  const durationInvalid = Boolean(durationRaw) && !isDurationValid(durationRaw);
  const titleMissing = !hasImport && !formData.title.trim();
  const durationMissing = !hasImport && !durationRaw;
  const canSubmit =
    linkStatus !== 'invalid' &&
    !titleMissing &&
    !durationMissing &&
    !durationInvalid;

  let titleError = formError.title;
  let durationError = formError.duration;
  if (showImport) {
    titleError =
      (touched.title || formError.title) && titleMissing
        ? t('pleaseAddTripTitle')
        : '';
    durationError = '';
    if (touched.duration || formError.duration) {
      if (durationInvalid) {
        durationError = t('pleaseAddValidTripDuration');
      } else if (durationMissing) {
        durationError = t('pleaseAddTripDuration');
      }
    }
  }

  const isSummaryStep = importStep === 'summary';
  // While the draft streams in and while the trip is being committed, the
  // top fields (Title/Dates/Duration/Destination) add nothing but dead
  // space above the progress UI, so they're visually collapsed (zero height,
  // not unmounted) for both steps. Keeping them in the DOM — rather than
  // removing them — anchors the modal to the same natural width as the form
  // step throughout reading/creating/summary, instead of the Dialog
  // shrinking to fit the much narrower progress text and then resizing again
  // as "Day N · <title>" changes length.
  const isProgressStep = importStep === 'reading' || importStep === 'creating';
  const isFormStep = importStep === 'form';

  // Built as plain JSX (an IIFE), not a nested component definition: a
  // `const ModalActions = () => (...)` declared inside the render body would
  // be a *new component type* on every CreateTrip render, forcing React to
  // unmount and remount the whole footer (and lose focus) on every keystroke.
  const modalActions = (() => {
    // The summary step's Discard/Go to trip actions are rendered by
    // TripImportSummary itself (see the body below), not the modal footer.
    if (isSummaryStep) {
      return null;
    }

    const isBusy = importStep === 'reading' || importStep === 'creating';

    return (
      <>
        <XGButton
          id='trip-modal-cancel-button'
          text={t('cancel', { ns: 'common' })}
          type='secondary'
          size='large'
          variant='outlined'
          onClick={handleCancel}
          disabled={importStep === 'creating'}
        />
        <Box sx={{ m: 1, position: 'relative', width: '50%' }}>
          <XGButton
            style={{ width: '100%' }}
            id='trip-modal-continue-button'
            text={hasImport ? t('tripImport.continue') : t('letsStart')}
            type='primary'
            size='large'
            variant='contained'
            onClick={handleConfirm}
            disabled={loading || isBusy || (showImport && !canSubmit)}
            startIcon={
              hasImport ? <Sparkle size='1em' weight='fill' /> : undefined
            }
          />
          {loading && !formData.outline && importStep === 'form' && (
            <CircularProgress
              size={24}
              sx={{
                color: blue[500],
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px'
              }}
            />
          )}
        </Box>
      </>
    );
  })();

  return (
    <Box id={id} data-testid='create-trip-view' {...rest}>
      <XGModal
        actions={modalActions}
        allowClosing={false}
        dismissable={false}
        id='create-trip-modal'
        open={openModal}
        className={`create-trip-modal${isSummaryStep ? ' create-trip-modal--summary' : ''}`}
        title={
          isSummaryStep ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Sparkle
                size='1em'
                weight='fill'
                style={{ color: 'var(--primary-600)', flexShrink: 0 }}
              />
              {t('tripImport.summary.title')}
            </Box>
          ) : isProgressStep ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Sparkle
                size='1em'
                weight='fill'
                style={{ color: 'var(--primary-600)', flexShrink: 0 }}
              />
              {t('tripImport.progress.header')}
            </Box>
          ) : (
            t('createTrip')
          )
        }
        labelledby={
          isSummaryStep
            ? t('tripImport.summary.title')
            : isProgressStep
              ? t('tripImport.progress.header')
              : t('createTrip')
        }
        maxWidth='lg'
        scroll={isSummaryStep ? 'paper' : 'body'}
      >
        <Box className='create-trip-content'>
          {/*
            Always rendered (visually collapsed outside the 'form' step, never
            unmounted): this is what anchors the modal to a consistent width
            across reading/creating/summary, instead of the Dialog shrinking
            to the progress text and resizing as it changes length. See
            .create-trip-fields--collapsed.
          */}
          <Box
            className={`create-trip-fields${isFormStep ? '' : ' create-trip-fields--collapsed'}`}
            aria-hidden={isFormStep ? undefined : true}
          >
            <Box className='create-trip-row'>
              <XGInput
                error={titleError !== ''}
                hint={titleError}
                label={`${t('title')}${hasImport ? '' : '*'}`}
                name='title'
                placeholder={t('enterTitle')}
                id='trip-title-input'
                type='text'
                value={formData.title}
                onChange={handleChange}
                onBlur={() => markTouched('title')}
                disabled={!isFormStep}
                inputProps={{
                  maxLength: 250
                }}
              />
              <span className='title-length-hint length-hint text-sm-medium'>
                {formData.title.length}/250
              </span>
            </Box>

            <Box className='create-trip-date'>
              <Box style={{ width: '70%' }}>
                <p className='MuiTypography-root MuiTypography-body2 create-trip__date-label'>
                  <span className='w-50'>{t('startDate')} </span>
                  <span className='w-50' style={{ marginLeft: '40px' }}>
                    {t('endDate')}
                  </span>
                </p>
                <MultiDateRangePicker
                  customClassName='nooverflow'
                  format={`${t('common:dateFormat')}`}
                  id='trip-start-date-input'
                  name='dateRange'
                  value={formData.dateRange}
                  onChange={(selectedDate) => {
                    handleOnDateRangeChange(selectedDate);
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ['clear']
                    }
                  }}
                  minDate={new Date()}
                  disabled={!isFormStep}
                />
              </Box>

              <Box className='duration-container'>
                <XGInput
                  error={durationError !== ''}
                  hint={durationError}
                  type='text'
                  value={formData.duration}
                  label={`${t('duration')}${hasImport ? '' : '*'}`}
                  name='duration'
                  id='trip-duration-input'
                  onInput={handleOnInputNumber}
                  onBlur={() => markTouched('duration')}
                  disabled={!isFormStep}
                  inputProps={{
                    maxLength: 3
                  }}
                />

                <span
                  className={`text-md-regular duration-placeholder ${
                    durationError && 'duration-placeholder-error'
                  }`}
                >
                  {t('days').toLowerCase()}
                </span>
              </Box>
            </Box>
            <div>
              <XGAutocomplete
                placeholder={t('searchByCountryOrCity')}
                name='destination'
                label={`${t('destination')}${formData.outline ? '*' : ''}`}
                id='trip-destination-input'
                options={destinations}
                disabled={!isFormStep}
                isOptionEqualToValue={(option, value) =>
                  option.title === value?.title
                }
                onChange={(event, newValue) => {
                  setFormData((prevData) => ({
                    ...prevData,
                    destination: newValue
                  }));
                  if (newValue) {
                    setFormError({ ...formError, destination: '' }); // Reset error state
                  }
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    color: formError.destination ? '#FF6E6E' : 'inherit'
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: formError.destination
                        ? '#FF6E6E'
                        : '#var(-gray-300)'
                    },
                    '&:hover fieldset': {
                      borderColor: formError.destination
                        ? '#FF6E6E'
                        : '#var(-gray-500)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: formError.destination
                        ? '#FF6E6E'
                        : '#var(-gray-300)'
                    }
                  }
                }}
              />
              {formError.destination && (
                <span
                  style={{
                    color: '#FF6E6E',
                    fontSize: '14px',
                    display: 'flex',
                    marginTop: '8px'
                  }}
                >
                  {formError.destination}
                </span>
              )}
            </div>
            {/*
              TODO: The outline field will be hidden until the AI functinality is fixed
            */}
            <div className='outline-container' style={{ display: 'none' }}>
              <XGTextarea
                id='trip-outline-textarea'
                label={t('outline')}
                value={formData.outline}
                minRows={12}
                maxRows={12}
                sx={{
                  width: '100%',
                  height: 'auto',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }}
                onChange={(e) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    outline: e.target.value
                  }))
                }
              />
              {submitPressed && formData.outline && (
                <Box sx={{ width: '100%', mt: 2 }}>
                  <LinearProgress variant='determinate' value={progress} />
                </Box>
              )}
            </div>
          </Box>

          {!isSummaryStep &&
            (isProgressStep ? (
              <TripImportProgress
                sourceKind={getImportSourceKind()}
                step={
                  importStep === 'creating'
                    ? 'creating'
                    : importProgress?.step || 'reading'
                }
                dayNumber={importProgress?.dayNumber}
                dayTitle={importProgress?.dayTitle}
              />
            ) : (
              showImport && (
                <TripImportSection
                  value={importValue}
                  allowDocuments={allowDocuments}
                  onChange={handleImportValueChange}
                  disabled={loading}
                  errorCode={importErrorCode}
                  onLogin={clearSessionAndGoToLogin}
                  onLinkStatusChange={setLinkStatus}
                />
              )
            ))}
        </Box>

        {isSummaryStep && (
          <TripImportSummary
            draft={draft}
            tripId={tripId}
            onDiscarded={handleDiscarded}
          />
        )}
      </XGModal>
    </Box>
  );
};

CreateTrip.propTypes = {
  destinations: PropTypes.arrayOf(
    PropTypes.shape({
      countryId: PropTypes.number,
      cityId: PropTypes.number,
      title: PropTypes.string
    })
  ),
  children: PropTypes.node,
  id: PropTypes.string.isRequired,
  openModal: PropTypes.bool,
  setOpenModal: PropTypes.func
};

export default CreateTrip;
